import React, { PropsWithChildren, useState } from 'react';
import { LuArrowRightLeft, LuEllipsis, LuTrash } from 'react-icons/lu';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { FeatureFlag } from '@ndvr/auth-api';
import { GlobalAccount } from '@ndvr/customer-api';

import { Breadcrumbs } from '@/components/Breadcrumbs';
import { CopyGuidDropdownMenuItem } from '@/components/CopyGuidComponents';
import { EntityUnavailable, isHttpError, useForgetNotFoundEntity } from '@/components/EntityUnavailable';
import { PageVersionSwitch } from '@/components/PageVersionSwitch';
import { RoutingTab, RoutingTabs, useActiveTab } from '@/components/RoutingTabs';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Page, PageContent, PageTitle, PageTitleRow, PageTopBar } from '@/components/ui/page-layout';
import { usePageVersion } from '@/hooks/usePageVersion';
import { AdvisorLoginButton } from 'modules';
import { themeDefault } from 'styles';
import { getHouseholdName, useHouseholdRoutePaths, useHouseholdRouteType, useRequiredParams } from 'utils';
import { useAuthContext } from 'views/auth/Auth';
import { useAssignedFlags } from 'views/auth/auth-queries';
import { useFeatureFlag } from 'views/auth/useFeatureFlag';
import { IconDots, PageSpinner, Text, Toast } from 'widgets';
import { DeleteValidationModal } from 'widgets/delete/DeleteValidationModal';
import { Portal } from 'widgets/Portal';

import { ConvertHouseholdTypeDialog } from './ConvertHouseholdTypeDialog';
import {
    useCommandCenterHouseholdProfile,
    useConvertClientToProspect,
    useConvertProspectToClient,
    useDeleteHousehold,
} from './household-queries';
import { REPORTING_FILTER_SEARCH_PARAMS } from './reporting/reporting-filters';

const HOUSEHOLD_HEADER_ACTIONS_PORTAL_ID = 'household-header-actions';

export const HouseholdIndividualPage = () => {
    const { isHouseholdRoute, isOnboardingRoute } = useHouseholdRouteType();
    const { householdTypeLabel } = useHouseholdRouteType();
    const { HouseholdIndividualPageRoutePath, HouseholdListPageRoutePath } = useHouseholdRoutePaths();
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const {
        data: accountData,
        isPending: isAccountsDataLoading,
        error: profileError,
    } = useCommandCenterHouseholdProfile(globalAccountGUID);
    const fileStorageEnabled = useFeatureFlag(FeatureFlag.GLOBAL_ACCOUNT_FILE_STORAGE);
    const billingEnabled = useFeatureFlag(FeatureFlag.CC_BILLING);
    const reportingEnabled = useFeatureFlag(FeatureFlag.CC_REPORTING);
    const callsEnabled = useFeatureFlag(FeatureFlag.CC_PHONE_CALLS);
    const { data: assignedFlags, isPending: isFlagsPending } = useAssignedFlags();
    const onboardingEnabled = assignedFlags?.includes(FeatureFlag.ONBOARDING);
    const {
        view: pageVersion,
        enabled: canSwitchPageVersion,
        isPending: isPageVersionPending,
        switchView,
    } = usePageVersion();
    const accountsPageEnabled = pageVersion === 'new';
    const location = useLocation();
    const { availableTenants } = useAuthContext();

    const isOnboardingInProgress = isHouseholdRoute && accountData?.isOnboardingInProgress === true;
    const tabs = householdTabs({
        isOnboardingInProgress,
        isHouseholdRoute,
        fileStorageEnabled: !!fileStorageEnabled,
        billingEnabled: !!billingEnabled,
        reportingEnabled: !!reportingEnabled,
        callsEnabled: !!callsEnabled,
        accountsPageEnabled: !!accountsPageEnabled,
        onboardingEnabled: !!onboardingEnabled,
    });
    const { activeEntry } = useActiveTab(HouseholdIndividualPageRoutePath(globalAccountGUID), tabs);
    const accountsPath = HouseholdIndividualPageRoutePath(globalAccountGUID, 'accounts');
    const portfolioPath = HouseholdIndividualPageRoutePath(globalAccountGUID, 'portfolio');
    const isVersionedPage = location.pathname === accountsPath || location.pathname === portfolioPath;
    const preferredPath = accountsPageEnabled ? accountsPath : portfolioPath;
    const showPageSwitcher = canSwitchPageVersion && isVersionedPage;

    useForgetNotFoundEntity(globalAccountGUID, profileError);

    if (isHttpError(profileError, 404)) {
        return (
            <EntityUnavailable
                title="This household no longer exists"
                backTo={HouseholdListPageRoutePath}
                backLabel={`Back to ${householdTypeLabel.toLowerCase()}s`}
            />
        );
    }

    if (isAccountsDataLoading || isPageVersionPending || isFlagsPending) {
        return <PageSpinner />;
    }

    if (isVersionedPage && location.pathname !== preferredPath) {
        return <Navigate to={{ pathname: preferredPath, search: location.search, hash: location.hash }} replace />;
    }

    if (!accountData) {
        return (
            <div className="flex items-center justify-center">
                <Text $themeColor="negative">No Household found for {globalAccountGUID}</Text>
            </div>
        );
    }

    if (
        location.pathname === HouseholdIndividualPageRoutePath(globalAccountGUID, 'onboarding') &&
        (!isOnboardingInProgress || !onboardingEnabled)
    ) {
        return <Navigate to={HouseholdIndividualPageRoutePath(globalAccountGUID, 'portfolio')} replace />;
    }

    const householdName = getHouseholdName(accountData.globalAccount);
    const clientBaseName = availableTenants.find((t) => t.tenantGUID === accountData.globalAccount.tenantGUID)?.name;

    return (
        <Page className="relative">
            {showPageSwitcher && <PageVersionSwitch isNew={accountsPageEnabled} onSwitch={switchView} />}
            <PageTopBar />
            <PageTitleRow
                actions={
                    <div className="ml-auto flex items-center gap-4">
                        <div id={HOUSEHOLD_HEADER_ACTIONS_PORTAL_ID} className="contents" />
                        <AdvisorLoginButton
                            globalAccountGUID={accountData.globalAccount.globalAccountGUID}
                            redirect="/overview"
                            size="icon"
                            variant="outline2"
                            className="size-9 rounded-full px-2"
                        />
                        <HouseholdOptionsMenu household={accountData.globalAccount} />
                    </div>
                }
            >
                <div className="flex flex-col gap-1">
                    {activeEntry && <PageTitle>{activeEntry.title}</PageTitle>}
                    <Breadcrumbs
                        parts={[
                            ...(clientBaseName ? [{ label: clientBaseName }] : []),
                            {
                                label: isOnboardingRoute ? 'Onboarding' : `${householdTypeLabel}s`,
                                path: HouseholdListPageRoutePath,
                            },
                            { label: householdName },
                        ]}
                    />
                </div>
            </PageTitleRow>
            <PageContent scroll="children" padding="none">
                {isOnboardingInProgress && !onboardingEnabled && (
                    <p role="status" className="text-muted-foreground px-6 py-3 text-sm">
                        Onboarding is not enabled for your account. You can view this household in Portfolios.
                    </p>
                )}
                <RoutingTabs
                    tabs={tabs}
                    fallback={
                        isOnboardingInProgress && onboardingEnabled
                            ? 'onboarding'
                            : accountsPageEnabled
                              ? 'accounts'
                              : 'portfolio'
                    }
                    baseUrl={HouseholdIndividualPageRoutePath(globalAccountGUID)}
                    activeColor={themeDefault.feature.account}
                >
                    <Outlet />
                </RoutingTabs>
            </PageContent>
        </Page>
    );
};

const HouseholdOptionsMenu = ({ household }: { household: GlobalAccount }) => {
    const navigate = useNavigate();
    const { isHouseholdRoute, isOnboardingRoute } = useHouseholdRouteType();
    const { HouseholdListPageRoutePath } = useHouseholdRoutePaths();
    const householdName = getHouseholdName(household);

    const { mutateAsync: deleteHousehold, isPending: isDeletePending } = useDeleteHousehold();
    const { mutate: convertProspectToClient, isPending: isConvertToClientPending } = useConvertProspectToClient();
    const { mutate: convertClientToProspect, isPending: isConvertToProspectPending } = useConvertClientToProspect();
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [convertDialogOpen, setConvertDialogOpen] = useState(false);

    const handleConvertConfirm = () => {
        if (isHouseholdRoute) {
            convertClientToProspect(household.globalAccountGUID, {
                onSuccess: () => {
                    setConvertDialogOpen(false);
                    navigate(`/prospect/${household.globalAccountGUID}`);
                },
            });
        } else {
            convertProspectToClient(household.globalAccountGUID, {
                onSuccess: () => {
                    setConvertDialogOpen(false);
                    navigate(`/household/${household.globalAccountGUID}`);
                },
            });
        }
    };

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="outline2" size="sm" className="rounded-full px-2" aria-label="Household actions">
                        <LuEllipsis className="h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    <CopyGuidDropdownMenuItem guid={household.globalAccountGUID} />
                    {!isOnboardingRoute && (
                        <DropdownMenuItem
                            onClick={() => setConvertDialogOpen(true)}
                            className="flex w-full items-center gap-2"
                        >
                            <span>
                                <LuArrowRightLeft className="h-4 w-4" />
                            </span>
                            <span>{isHouseholdRoute ? 'Convert to Prospect' : 'Convert to Client'}</span>
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                        onClick={() => setIsDeleteModalOpen(true)}
                        className="text-destructive focus:text-destructive flex w-full items-center gap-2"
                    >
                        <LuTrash className="h-4 w-4" />
                        Delete
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
            <ConvertHouseholdTypeDialog
                open={convertDialogOpen}
                onOpenChange={setConvertDialogOpen}
                onConfirm={handleConvertConfirm}
                isPending={isConvertToClientPending || isConvertToProspectPending}
                direction={isHouseholdRoute ? 'toProspect' : 'toClient'}
                householdName={householdName}
            />
            {isDeleteModalOpen && (
                <DeleteValidationModal
                    recordName={householdName}
                    disabled={isDeletePending}
                    onDelete={async () => {
                        const result = await deleteHousehold(household.globalAccountGUID);
                        if (result?.valid === true) {
                            setIsDeleteModalOpen(false);
                            Toast.success('Household deleted');
                            navigate(HouseholdListPageRoutePath);
                        }
                        return result;
                    }}
                    onClose={() => setIsDeleteModalOpen(false)}
                />
            )}
        </>
    );
};

export const HouseholdHeaderActionsPortal = ({ children }: PropsWithChildren) => (
    <Portal id={HOUSEHOLD_HEADER_ACTIONS_PORTAL_ID}>{children}</Portal>
);

export const householdTabs = ({
    isHouseholdRoute,
    isOnboardingInProgress,
    fileStorageEnabled,
    billingEnabled,
    reportingEnabled,
    callsEnabled,
    accountsPageEnabled,
    onboardingEnabled,
}: {
    isHouseholdRoute: boolean;
    isOnboardingInProgress: boolean;
    fileStorageEnabled: boolean;
    billingEnabled: boolean;
    reportingEnabled: boolean;
    callsEnabled: boolean;
    accountsPageEnabled: boolean;
    /** The Onboarding tab is behind the ONBOARDING flag, and requires onboarding in progress. */
    onboardingEnabled: boolean;
}): RoutingTab[] => [
    // An onboarding household is mostly worked on through its onboarding, so that tab leads.
    ...(isOnboardingInProgress && onboardingEnabled
        ? [{ type: 'link' as const, title: 'Onboarding', url: '/onboarding' }, { type: 'separator' as const }]
        : []),
    // PM section
    ...(accountsPageEnabled ? [{ type: 'link' as const, title: 'Accounts', url: '/accounts' }] : []),
    { type: 'link', title: 'Portfolios', url: '/portfolio', hidden: accountsPageEnabled },
    ...(reportingEnabled && isHouseholdRoute
        ? [
              {
                  type: 'link' as const,
                  title: 'Reporting',
                  url: '/reporting',
                  preserveSearchParams: REPORTING_FILTER_SEARCH_PARAMS,
                  // The page pads itself so its sticky filter bar can pin flush at the top.
                  noPadding: true,
              },
          ]
        : []),
    ...(isHouseholdRoute ? [{ type: 'link' as const, title: 'Wash Sale Groups', url: '/tools', noPadding: true }] : []),
    { type: 'separator' },
    // CRM section
    { type: 'link', title: 'Notes', url: '/notes', noPadding: true },
    {
        type: 'link',
        title: callsEnabled ? 'Messages & Calls' : 'Messages',
        url: '/messages',
        noPadding: true,
        // Detail routes this tab owns: RoutingTabs renders no content while no tab matches the URL.
        aliases: ['/calls', '/emails', '/texts'],
    },
    { type: 'link', title: 'Tasks', url: '/tasks', noPadding: true },
    { type: 'link', title: 'Events', url: '/events', noPadding: true },
    { type: 'link', title: 'Opportunities', url: '/opportunities', noPadding: true },
    ...(fileStorageEnabled || (isHouseholdRoute && onboardingEnabled)
        ? [{ type: 'link' as const, title: 'Files', url: '/files' }]
        : []),
    { type: 'separator' },
    // Billing section
    ...(isHouseholdRoute
        ? [
              {
                  type: 'menu' as const,
                  title: 'Billing',
                  items: [
                      ...(billingEnabled ? [{ title: 'Billing Groups', url: '/billing-groups' }] : []),
                      { title: 'Billing Setup', url: '/billing-setup' },
                      { title: 'Billing History', url: '/billing-history' },
                  ],
              },
              { type: 'separator' as const },
          ]
        : []),
    // Client section
    { type: 'link', title: 'Members', url: '/members' },
    { type: 'link', title: 'Details', url: '/details', dataTestId: 'details-tab' },
    {
        type: 'menu',
        title: <IconDots />,
        items: [{ title: 'Account Owners', url: '/owners' }],
    },
];
